import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Thermometer, AlertTriangle, CheckCircle, Clock, Snowflake, Zap } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { temperatureZoneService, type TemperatureReading, type ColdChainEvent } from '../../services/temperatureZone';

export const TemperatureMonitor: React.FC = () => {
  const [readings, setReadings] = useState<TemperatureReading[]>([]);
  const [events, setEvents] = useState<ColdChainEvent[]>([]);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);

  useEffect(() => {
    // Simulate real-time temperature readings
    const interval = setInterval(() => {
      const zones = temperatureZoneService.getAllZones();
      
      zones.forEach(zone => {
        // Generate realistic temperature readings with some variance
        const baseTemp = (zone.minTemp + zone.maxTemp) / 2;
        const variance = Math.random() * 4 - 2; // ±2 degrees
        const temperature = baseTemp + variance;
        
        temperatureZoneService.addTemperatureReading({
          zoneId: zone.id,
          temperature,
          humidity: 45 + Math.random() * 20, // 45-65% humidity
          timestamp: new Date().toISOString(),
          sensorId: `sensor-${zone.id}-01`
        });
      });
      
      setReadings(temperatureZoneService.getAllCurrentReadings());
      setEvents(temperatureZoneService.getColdChainEvents({ resolved: false }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getTemperatureIcon = (zoneType: string) => {
    switch (zoneType) {
      case 'frozen':
        return <Snowflake className="w-5 h-5 text-blue-600" />;
      case 'chilled':
        return <Zap className="w-5 h-5 text-green-600" />;
      default:
        return <Thermometer className="w-5 h-5 text-orange-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const zones = temperatureZoneService.getAllZones();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Temperature Monitoring</h2>
          <p className="text-gray-600">Real-time cold chain compliance monitoring</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-600">Live monitoring</span>
        </div>
      </div>

      {/* Temperature Zones Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {zones.map(zone => {
          const reading = readings.find(r => r.zoneId === zone.id);
          const hasAlerts = events.some(e => e.zoneId === zone.id);
          
          return (
            <motion.div
              key={zone.id}
              whileHover={{ scale: 1.02 }}
              onClick={() => setSelectedZone(zone.id)}
              className="cursor-pointer"
            >
              <Card className={`relative ${selectedZone === zone.id ? 'ring-2 ring-blue-500' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      zone.type === 'frozen' ? 'bg-blue-100' :
                      zone.type === 'chilled' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      {getTemperatureIcon(zone.type)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{zone.name}</h3>
                      <p className="text-sm text-gray-600">{zone.type}</p>
                    </div>
                  </div>
                  
                  {hasAlerts && (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                </div>

                {reading ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">
                        {reading.temperature.toFixed(1)}°C
                      </span>
                      <Badge variant={
                        reading.status === 'normal' ? 'success' :
                        reading.status === 'warning' ? 'warning' : 'error'
                      }>
                        {reading.status}
                      </Badge>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <p>Range: {zone.minTemp}°C to {zone.maxTemp}°C</p>
                      {reading.humidity && (
                        <p>Humidity: {reading.humidity.toFixed(1)}%</p>
                      )}
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          reading.status === 'normal' ? 'bg-green-500' :
                          reading.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{
                          width: `${Math.min(100, Math.max(0, 
                            ((reading.temperature - zone.minTemp) / (zone.maxTemp - zone.minTemp)) * 100
                          ))}%`
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No data available</p>
                  </div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Active Alerts */}
      {events.length > 0 && (
        <Card>
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h3 className="text-lg font-medium text-gray-900">Active Alerts</h3>
            <Badge variant="error">{events.length}</Badge>
          </div>
          
          <div className="space-y-3">
            {events.slice(0, 5).map(event => {
              const zone = zones.find(z => z.id === event.zoneId);
              
              return (
                <div key={event.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${
                      event.severity === 'critical' ? 'bg-red-500' :
                      event.severity === 'high' ? 'bg-orange-500' :
                      event.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`} />
                    
                    <div>
                      <p className="font-medium text-gray-900">
                        {zone?.name || event.zoneId}
                      </p>
                      <p className="text-sm text-gray-600">
                        {event.type.replace('_', ' ')} - {event.notes}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <Badge variant={
                    event.severity === 'critical' ? 'error' :
                    event.severity === 'high' ? 'warning' : 'neutral'
                  }>
                    {event.severity}
                  </Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Zone Details */}
      {selectedZone && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Zone Details</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedZone(null)}
            >
              Close
            </Button>
          </div>
          
          {(() => {
            const zone = zones.find(z => z.id === selectedZone);
            const reading = readings.find(r => r.zoneId === selectedZone);
            const zoneEvents = events.filter(e => e.zoneId === selectedZone);
            
            if (!zone) return null;
            
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Current Temperature</p>
                    <p className="text-xl font-bold text-gray-900">
                      {reading ? `${reading.temperature.toFixed(1)}°C` : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Target Range</p>
                    <p className="text-lg font-medium text-gray-900">
                      {zone.minTemp}°C - {zone.maxTemp}°C
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Max Pick Time</p>
                    <p className="text-lg font-medium text-gray-900">
                      {zone.maxPickTime} min
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <Badge variant={
                      reading?.status === 'normal' ? 'success' :
                      reading?.status === 'warning' ? 'warning' : 'error'
                    }>
                      {reading?.status || 'Unknown'}
                    </Badge>
                  </div>
                </div>
                
                {zone.requiredEquipment && zone.requiredEquipment.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Required Equipment</p>
                    <div className="flex flex-wrap gap-2">
                      {zone.requiredEquipment.map(equipment => (
                        <Badge key={equipment} variant="neutral">
                          {equipment.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {zoneEvents.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Recent Events</p>
                    <div className="space-y-2">
                      {zoneEvents.slice(0, 3).map(event => (
                        <div key={event.id} className="text-sm p-2 bg-gray-50 rounded">
                          <p className="font-medium">{event.type.replace('_', ' ')}</p>
                          <p className="text-gray-600">{event.notes}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </Card>
      )}
    </div>
  );
};